import React from "react";
import {Col, Row} from "antd";
import {getTranslate, Translate, TranslateFunction} from "react-localize-redux";
import {connect} from "react-redux";
import {checkTranslation, Nullable} from "@data-force/app";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import ReactMarkdown from "react-markdown";
import classNames from "classnames";
import StoreState from "../../interfaces/store/storeState";
import {PaymentStats, TaskPhaseStats} from "../../interfaces/store/actions/payload/task";
import "./TaskDetails.scss";
import {
    formatString, getFormattedTime, getJobPhaseStatus,
} from "../../utils/common";
import {JobEntity} from "../../interfaces/store/actions/payload/job";
import {ContentType} from "../../enums/enums";

interface StateProps {
    data: JobEntity
    activeJobType: string
    translate: TranslateFunction
    isTest: Nullable<boolean>
}

type Props = StateProps;

export const TaskDetails: React.FC<Props> = (props: Props): React.ReactElement => {
    dayjs.extend(utc);
    const translatePath: string = "components.common.TaskDetails.TaskDetails";
    const translatePathProjectType: string = "enums.ProjectType";
    const translatePathTestType: string = "enums.TestType";
    const translatePathTestSkill: string = "enums.TestSkill";
    const allowedJobStatuses: ContentType[] = [ContentType.IN_PROGRESS, ContentType.COMPLETED];

    const {
        data,
        activeJobType,
        translate,
        isTest,
    }: Props = props;
    const {
        projectType = "",
        estimatedTime,
        phaseStats = {},
        dueDate,
        taskCount = 0,
        description = "",
        jobCodename = "",
        minimumScoreThreshold,
        testSkill = "",
        testType = "",
        timeLimit,
        paymentStats,
        userAvailableCount,
    }: JobEntity = data;
    const {REJECTED = 0, COMPLETED = 0}: TaskPhaseStats = phaseStats;
    const {APPROVED = 0}: PaymentStats = paymentStats || {};

    const dueDateFormat: string = translate("dateFormat.text") as string;
    const notAssigned: string = translate(`${translatePath}.notAssigned.text`) as string;
    const testCodeNameFormat: string = translate(`${translatePath}.testIdPattern.text`) as string;
    const testCodeName: string = formatString(testCodeNameFormat, jobCodename);
    const acceptedTasksClasses: string = classNames({
        "df-accepted-tasks": COMPLETED,
    });
    const rejectedTasksClasses: string = classNames({
        "df-rejected-tasks": REJECTED,
    });

    const taskStatus: string = getJobPhaseStatus(data);
    const codeNameFormat: string = props.translate(`${translatePath}.codeNameFormat.text`) as string;
    const formattedCodename: string = formatString(codeNameFormat, jobCodename);

    return (
        <div className="df-task-details-container">
            {!isTest ? (
                <>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.projectTypeLabel.text`}/></Col>
                        <Col span={12}>{checkTranslation(`${translatePathProjectType}.${projectType}.text`, projectType, translate)}</Col>
                    </Row>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.codeNameLabel.text`}/></Col>
                        <Col span={12}>{formattedCodename}</Col>
                    </Row>
                </>
            ) : (
                <>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.testTypeLabel.text`}/></Col>
                        <Col span={12}>{checkTranslation(`${translatePathTestType}.${testType}.text`, testType, translate)}</Col>
                    </Row>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.testIdLabel.text`}/></Col>
                        <Col span={12}>{testCodeName}</Col>
                    </Row>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.testSkill.text`}/></Col>
                        <Col span={12}>{checkTranslation(`${translatePathTestSkill}.${testSkill}.text`, testSkill, translate)}</Col>
                    </Row>
                </>
            )}
            {!!estimatedTime && (
                <Row>
                    <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.estimatedTimeLabel.text`}/></Col>
                    <Col span={12}><span>{getFormattedTime(estimatedTime, translate)}</span></Col>
                </Row>
            )}
            {isTest ? (
                <>
                    {!!timeLimit && (
                        <Row>
                            <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.maxTimeAvailable.text`}/></Col>
                            <Col span={12}><span>{getFormattedTime(timeLimit, translate)}</span></Col>
                        </Row>
                    )}
                    {!!userAvailableCount && (
                        <Row>
                            <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.numberOfAttempts.text`}/></Col>
                            <Col span={12}>{userAvailableCount}</Col>
                        </Row>
                    )}
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.passingScore.text`}/></Col>
                        <Col span={12}>
                            {minimumScoreThreshold ? (
                                formatString(
                                    translate(`${translatePath}.percentagePlaceholder.text`) as string, minimumScoreThreshold.toString(),
                                )
                            ) : <Translate id={`${translatePath}.notAvailable.text`}/>}
                        </Col>
                    </Row>
                </>
            ) : (
                <>
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.tasksLabel.text`}/></Col>
                        <Col span={12}>{taskCount}</Col>
                    </Row>
                    {allowedJobStatuses.includes(activeJobType as ContentType) && (
                        <>
                            <Row>
                                <Col span={12} className="df-column-labels">
                                    <Translate id={`${translatePath}.completedTasksLabel.text`}/>
                                </Col>
                                <Col span={12} className={acceptedTasksClasses}>{COMPLETED}</Col>
                            </Row>
                            <Row>
                                <Col span={12} className="df-column-labels">
                                    <Translate id={`${translatePath}.rejectedTasksLabel.text`}/>
                                </Col>
                                <Col span={12} className={rejectedTasksClasses}>{REJECTED}</Col>
                            </Row>
                        </>

                    )}
                    {activeJobType === ContentType.COMPLETED && paymentStats && (
                        <Row>
                            <Col span={12} className="df-column-labels">
                                <Translate id={`${translatePath}.approvedForPaymentLabel.text`}/>
                            </Col>
                            <Col span={12}>{APPROVED}</Col>
                        </Row>
                    )}
                    <Row>
                        <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.dueDateLabel.text`}/></Col>
                        <Col span={12}>{dueDate ? dayjs(dueDate).format(dueDateFormat) : notAssigned}</Col>
                    </Row>
                    {allowedJobStatuses.includes(activeJobType as ContentType) && (
                        <Row>
                            <Col span={12} className="df-column-labels"><Translate id={`${translatePath}.statusLabel.text`}/></Col>
                            <Col span={12}>{translate(`enums.TaskStatus.${taskStatus}.text`)}</Col>
                        </Row>
                    )}
                </>
            )}

            <Row>
                <Col span={24} className="df-column-labels"><Translate id={`${translatePath}.description.text`}/></Col>
                <Col className="df-task-details-description">
                    <ReactMarkdown linkTarget="_blank">{description}</ReactMarkdown>
                </Col>
            </Row>
        </div>
    );
};

function mapStateToProps(state: StoreState): StateProps {
    return {
        isTest: state.job.isTest,
        data: state.job.selectedJob || {} as JobEntity,
        activeJobType: state.job.activeJobType,
        translate: getTranslate(state.localize),
    };
}

export default connect(mapStateToProps)(TaskDetails);
